import { Alert, Box, Button } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider, type FieldValues, type SubmitHandler } from "react-hook-form";
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogBodyMount } from "./index";
import { getCurrentUserRoles, isViewer } from "../../utils/authorization";
import type { z } from "zod";
import { drawerButtonCancelProps, drawerButtonSaveProps } from "../../lib/ui/pageButtons";
import RightDrawer, { type RightDrawerWidth } from "../RightDrawer";

type ShowOptions = {
  onClose?: () => void;
  /** Values passed to `react-hook-form` `reset` when the dialog opens. */
  getDefaultValues?: (item: unknown) => Record<string, unknown>;
};

export type RhfFormDialogProps = {
  submitAction?: (formJson: Record<string, string>) => void | boolean | Promise<void | boolean>;
  body: (item: unknown) => React.ReactNode;
  maxWidth?: RightDrawerWidth | false;
  fullWidth?: boolean;
  /** Zod schema: wired as `zodResolver` (typed values before stringify). */
  validationSchema?: z.ZodType<FieldValues>;
};

const toStringRecord = (data: FieldValues): Record<string, string> => {
  const o: Record<string, string> = {};
  for (const [k, v] of Object.entries(data)) {
    if (v === undefined || v === null) o[k] = "";
    else if (typeof v === "string") o[k] = v;
    else o[k] = String(v);
  }
  return o;
};

/**
 * Like `GenericDialog` but uses React Hook Form. Children should use
 * `useFormContext` / `FormTextField` (see `lib/form`).
 */
const RhfFormDialog = React.forwardRef<
  { showDialog: (opts: Record<string, unknown>, item?: unknown, options?: ShowOptions) => void; closeDialog: () => void },
  RhfFormDialogProps
>((props, ref) => {
  const { submitAction, body, maxWidth = "sm", validationSchema } = props;
  const { t } = useTranslation();
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | null>(null);
  const [item, setItem] = useState<unknown>(null);
  const [open, setOpen] = useState(false);
  const [viewerRole, setViewerRole] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const getDefaultValuesRef = useRef<ShowOptions["getDefaultValues"] | undefined>(undefined);

  const methods = useForm<FieldValues>({
    resolver: validationSchema ? (zodResolver(validationSchema) as any) : undefined,
    defaultValues: {},
    mode: "onTouched",
  });

  const [dialogOptions, setDialogOptions] = useState({
    submitButtonText: "",
    cancelButtonText: "",
    title: "",
    content: "",
  });

  const close = useCallback(() => {
    setOpen(false);
    setServerError(null);
    methods.clearErrors();
    onCloseCallback?.();
  }, [methods, onCloseCallback]);

  useImperativeHandle(
    ref,
    () => ({
      showDialog(opts: Record<string, unknown>, nextItem?: unknown, options: ShowOptions = {}) {
        if (options.onClose) {
          setOnCloseCallback(() => options.onClose);
        } else {
          setOnCloseCallback(null);
        }
        getDefaultValuesRef.current = options.getDefaultValues;
        setItem(nextItem ?? null);
        setDialogOptions({
          submitButtonText: t("common.submit"),
          cancelButtonText: t("common.cancel"),
          title: t("common.genericDialog"),
          content: t("common.genericDialogContent"),
          ...(opts as object),
        } as typeof dialogOptions);
        setServerError(null);
        setOpen(true);
      },
      closeDialog: close,
    }),
    [close]
  );

  useEffect(() => {
    if (!open) return;
    const def = getDefaultValuesRef.current?.(item) ?? (item as Record<string, unknown>) ?? {};
    methods.reset(def);
  }, [open, item, methods]);

  useEffect(() => {
    getCurrentUserRoles().then((roles) => setViewerRole(isViewer(roles)));
  }, []);

  const onValid: SubmitHandler<FieldValues> = useCallback(
    async (data) => {
      if (viewerRole) {
        close();
        return;
      }
      if (!submitAction) {
        close();
        return;
      }
      const formJson = toStringRecord(data);
      try {
        const shouldClose = await submitAction(formJson);
        if (shouldClose !== false) {
          close();
        }
      } catch (e) {
        console.error(e);
        setServerError(e instanceof Error ? e.message : t("common.requestFailed"));
      }
    },
    [submitAction, viewerRole, close]
  );

  const onInvalid = useCallback(() => {
    setServerError(t("common.fixFields"));
  }, []);

  return (
    <FormProvider {...methods}>
      <RightDrawer
        open={open}
        onClose={close}
        title={dialogOptions.title}
        subtitle={dialogOptions.content}
        width={maxWidth}
        component="form"
        componentProps={{
          onSubmit: methods.handleSubmit(onValid, onInvalid),
          noValidate: true,
        }}
        footer={
          <>
            <Button {...drawerButtonCancelProps} type="button" onClick={close}>
              {dialogOptions.cancelButtonText}
            </Button>
            <Button {...drawerButtonSaveProps} type="submit" disabled={viewerRole}>
              {dialogOptions.submitButtonText}
            </Button>
          </>
        }
      >
        {serverError && (
          <Box mb={1.5}>
            <Alert severity="error" onClose={() => setServerError(null)} variant="outlined" sx={{ py: 0 }}>
              {serverError}
            </Alert>
          </Box>
        )}
        {open ? <DialogBodyMount body={body} item={item} /> : null}
      </RightDrawer>
    </FormProvider>
  );
});

RhfFormDialog.displayName = "RhfFormDialog";

export default RhfFormDialog;
